import { addToast } from '@mobi/component-library/Feedback/Toast'
import { MfaError, MFAMethod, MFAServerMethod } from '@mobi/api-types/src/mfa'
import { trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { MfaAnalyticsData } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { incrementQuickCounter } from '@core/Services/Analytics/helpers/quickCounter'

export class VerificationStandardError extends Error {}

const MFA_ERROR_MESSAGES = {
  RATE_LIMITED: 'Attempt limit exceeded, please try after some time.',
  CODE_MISMATCH: 'The verification code you entered is incorrect. Please check and try again.',
}

export function isRateLimitError(error: string): boolean {
  return (
    error === MFA_ERROR_MESSAGES.RATE_LIMITED ||
    error.toLowerCase().includes('limit exceeded') ||
    error.toLowerCase().includes('too many unsuccessful attempts')
  )
}

export function isCodeMismatchError(error: string): boolean {
  return (
    error === MFA_ERROR_MESSAGES.CODE_MISMATCH ||
    error.toLowerCase().includes('sorry the code you have provided is incorrect') ||
    error.toLowerCase().includes('invalid verification code')
  )
}

export function handleMfaError(
  mfaError: MfaError,
  operation: 'verification' | 'update' | 'complete' | 'authentication' = 'verification'
): void {
  const { status, error, message } = mfaError

  // Handle rate limiting (429)

  if (status === 429 || error === 'LimitExceededException') {
    addToast({
      id: 'mfa-rate-limit-error',
      isDismissable: true,
      title: 'Too many verification attempts',
      subtitle: 'Please try again later',
      icon: 'warning',
    })
    return
  }

  // Handle expired code
  if (error === 'ExpiredCodeException' || (message && message.includes('expired'))) {
    addToast({
      id: 'mfa-expired-code-error',
      isDismissable: true,
      title: 'Your verification code has expired',
      subtitle: 'Please request a new code',
      icon: 'error',
    })
    return
  }

  // Handle code mismatch
  if (error === 'CodeMismatchException' || (message && message.includes('match'))) {
    addToast({
      id: 'mfa-code-mismatch-error',
      isDismissable: true,
      title: 'Incorrect verification code',
      subtitle: 'Please check and try again',
      icon: 'error',
    })
    return
  }

  // Handle invalid parameter
  if (error === 'InvalidParameterException' && message) {
    addToast({
      id: 'mfa-invalid-parameter-error',
      isDismissable: true,
      title: message || 'Invalid request',
      subtitle: 'Please try again',
      icon: 'error',
    })
    return
  }

  if (status === 403 || error === 'Forbidden' || error === 'NotAuthorizedException') {
    let subtitle = 'Please try again.'
    if (message?.toLowerCase().includes('limit exceeded')) {
      subtitle = message
    }

    addToast({
      id: 'mfa-authentication-error',
      isDismissable: true,
      title: 'Authentication failed',
      subtitle: `${subtitle}`,
      icon: 'error',
    })
    return
  }

  // Default error handling
  const operationText = {
    verification: 'Error verifying your identity',
    update: 'Error verifying your contact',
    complete: 'Error completing MFA challenge',
    authentication: 'Error verifying your identity',
  }[operation]

  addToast({
    id: `mfa-${operation}-error`,
    isDismissable: true,
    title: operationText,
    subtitle: message || 'Please try again',
    icon: 'error',
  })
}

export function mfaCodeText(displayFactor: MFAMethod | MFAServerMethod, contactDetail: string) {
  const baseText = 'To access your account, enter the 6 digit code we sent to'
  switch (displayFactor) {
    case 'SMS':
      return `${baseText} your mobile number ${contactDetail}.`
    case 'EMAIL':
    case 'Email':
      return `${baseText} your email address ${contactDetail}.`
    default:
      return `${baseText} ${contactDetail}.`
  }
}

export function isMfaError(error: unknown): error is MfaError {
  if (typeof error !== 'object' || error === null) {
    return false
  }
  return error && typeof error === 'object' && 'status' in error && 'error' in error
}

export function getMFAMethodLabel(method: MFAMethod): string {
  switch (method) {
    case 'SMS':
      return 'Phone number'
    case 'Email':
      return 'Email'
    default:
      return method
  }
}

export function trackMfaEvent(data: MfaAnalyticsData) {
  if (data.otpMethod === 'EMAIL') {
    data.otpMethod = 'Email'
  }
  // explicitly set all properties to undefined because this
  // apparently makes tracking in GTA easier
  trackEvent('mfa', {
    otpMethod: undefined,
    message: undefined,
    activeState: undefined,
    skipped: undefined,
    trustedDeviceCount: undefined,
    ...data,
  })
  incrementQuickCounter(
    `mfa-ga-${normalizeForQuickCounterKey(data.interaction_type)}-${normalizeForQuickCounterKey(data.otpFlow)}`
  )
}

function normalizeForQuickCounterKey(str: string) {
  return str.replaceAll(' ', '-').toLowerCase()
}
